import React from "react";
import PropTypes from "prop-types";
import { useFlag } from "@unleash/proxy-client-react";

import { useTranslation } from "react-i18next";
import { ADD_WINE_TO_NEXT_BOX } from "../../../graphql/mutations";
import { GET_MEMBER_SHOPPING_CART, GET_MEMBER_SUBSCRIPTION } from "../../../graphql/queries";
import { isLoggedIn } from "../../../helpers/auth";
import { clearGuestBoxIntent, markGuestDirectAddsChosen } from "../../../helpers/guestCheckout";
import { showSuccessAlert } from "../../../helpers/alerts";
import { getWineBaseColor } from "../../../helpers/tools";

import Button from "../../atoms/Button";
import ButtonMutation from "../../atoms/ButtonMutation";
import AddProductToShoppingCartButton from "../../atoms/AddProductToShoppingCartButton";
import cellarBottleImg from "../../../assets/images/temp/bottle-xl-vertical.png";
import { AddToBoxOrCartSuccessMessage } from "../../atoms/AddToBoxOrCartSuccessMessage";
import { useLayoutContext } from "../../organisms/Layout/context";
import GuestBoxAddActions from "../GuestBoxAddActions";
import ProductAddTray from "../ProductAddTray";
import { I18N_NAMESPACES, UNLEASH_GUEST_CHECKOUT } from "../../../helpers/constants";

const WinesAddBottleTray = ({
  isOpened = false,
  selectedWine,
  memberId,
  isInBoxAndCartTray = false,
  onCloseTray = () => {},
  onCallBack = () => {},
}) => {
  const { setShouldOpenBoxAndCartTray } = useLayoutContext();
  const { t } = useTranslation([I18N_NAMESPACES.COMMON]);
  const isGuestCheckoutEnabled = useFlag(UNLEASH_GUEST_CHECKOUT);

  // Doesn't render the right panel if no data has been passed
  if (!selectedWine) {
    return null;
  }

  const { product, wineType } = selectedWine || {};
  const bottleImage = product?.productPhotos?.[0]?.photoLarge || cellarBottleImg;

  const { id: wineTypeId, wineClass } = wineType || {};
  const { id: wineClassId } = wineClass || {};

  /**
   * Closes the tray after adding bottle, and calls the callback function.
   * */
  const closeTrayAndCallBack = () => {
    onCloseTray();
    onCallBack();
  };

  const successMessage = (
    <AddToBoxOrCartSuccessMessage
      values={{ count: selectedWine.quantity || 1, isBox: true }}
      i18nKey="winePages:wineAdded"
      setShouldOpenBoxAndCartTray={setShouldOpenBoxAndCartTray}
    />
  );

  const addBottleToBoxButtonComponent = (
    <ButtonMutation
      label={t("common:addToMyBox")}
      mutationProp={ADD_WINE_TO_NEXT_BOX}
      reFetchQueriesProp={[{ query: GET_MEMBER_SUBSCRIPTION }, { query: GET_MEMBER_SHOPPING_CART }]}
      input={{
        wineId: selectedWine.id,
        quantity: selectedWine.quantity,
        memberId,
      }}
      handleResponse={() => {
        closeTrayAndCallBack();
        showSuccessAlert(successMessage, {});
      }}
      ButtonComponent={Button}
      awaitRefetchQueries
      full
      lg
    />
  );

  const addBottleToCartButtonComponent = (
    <AddProductToShoppingCartButton
      product={selectedWine.product}
      quantity={selectedWine.quantity}
      label={t("common:addToOneOff")}
      showShoppingCart={false}
      callbackAfterAdd={() => {
        // Remember the guest's answer so the tray stops asking on every add. An
        // explicit one-off choice also drops any earlier box intent.
        if (!isLoggedIn()) {
          if (isGuestCheckoutEnabled) markGuestDirectAddsChosen();
          clearGuestBoxIntent();
        }
        closeTrayAndCallBack();
      }}
      full
      lg
      ghost
    />
  );

  const guestBoxAddActions =
    !isLoggedIn() && isGuestCheckoutEnabled ? (
      <GuestBoxAddActions item={selectedWine} onAdded={closeTrayAndCallBack} />
    ) : null;

  return (
    <ProductAddTray
      isInBoxAndCartTray={isInBoxAndCartTray}
      isOpened={isOpened}
      onCloseTray={() => onCloseTray()}
      bgColor={getWineBaseColor(wineClassId, wineTypeId)}
      bottleImage={bottleImage}
      addBottleToBoxButtonComponent={
        isLoggedIn() && memberId ? addBottleToBoxButtonComponent : guestBoxAddActions
      }
      addBottleToCartButtonComponent={addBottleToCartButtonComponent}
    />
  );
};

WinesAddBottleTray.propTypes = {
  memberId: PropTypes.number,
  isOpened: PropTypes.bool,
  selectedWine: PropTypes.shape({}),
  isInBoxAndCartTray: PropTypes.bool,
  onCloseTray: PropTypes.func,
  onCallBack: PropTypes.func,
};

export default WinesAddBottleTray;
